export type TemplateBuilderOperationDomain =
  | 'document'
  | 'figma'
  | 'animation'
  | 'preview'
  | 'persistence';

export const DOCUMENT_OPERATIONS = [
  'set_name',
  'set_background',
  'set_business_entity',
  'set_brand_kit',
  'select_element',
  'rename_element',
  'move_element',
  'reorder_element',
  'remove_element',
  'add_element',
  'update_element_data',
  'set_element_locked',
  'set_element_hidden',
  'set_element_sources',
  'remove_element_source',
  'add_data_source',
  'remove_data_source',
  'set_display_options',
  'set_element_date_format',
  'set_element_odds_format',
  'set_element_penalties_format',
] as const;

export const ANIMATION_OPERATIONS = [
  'apply_animation_effect',
  'update_animation_effect',
  'remove_animation_effect',
] as const;

export const WORKING_COPY_OPERATIONS = [...DOCUMENT_OPERATIONS, ...ANIMATION_OPERATIONS] as const;

export const UNSUPPORTED_OPERATIONS = ['group_elements'] as const;

export const SAVE_OPERATIONS = ['commit_working_copy'] as const;

export const RESTORE_OPERATIONS = ['stage_saved_version'] as const;

export const TEMPLATE_BUILDER_OPERATIONS = [
  ...WORKING_COPY_OPERATIONS,
  ...RESTORE_OPERATIONS,
  ...SAVE_OPERATIONS,
  ...UNSUPPORTED_OPERATIONS,
] as const;

export type TemplateBuilderOperation = (typeof TEMPLATE_BUILDER_OPERATIONS)[number];
export type AnimationOperation = (typeof ANIMATION_OPERATIONS)[number];

export type OperationAvailability = {
  animationsEnabled: boolean;
};

const DOMAINS: Record<TemplateBuilderOperation, TemplateBuilderOperationDomain> = {
  set_name: 'document',
  set_background: 'document',
  set_business_entity: 'document',
  set_brand_kit: 'document',
  select_element: 'document',
  rename_element: 'document',
  move_element: 'document',
  reorder_element: 'document',
  remove_element: 'document',
  add_element: 'document',
  update_element_data: 'document',
  set_element_locked: 'document',
  set_element_hidden: 'document',
  set_element_sources: 'document',
  remove_element_source: 'document',
  add_data_source: 'document',
  remove_data_source: 'document',
  set_display_options: 'document',
  set_element_date_format: 'document',
  set_element_odds_format: 'document',
  set_element_penalties_format: 'document',
  apply_animation_effect: 'animation',
  update_animation_effect: 'animation',
  remove_animation_effect: 'animation',
  group_elements: 'document',
  commit_working_copy: 'persistence',
  stage_saved_version: 'persistence',
};

const SUMMARIES: Record<TemplateBuilderOperation, string> = {
  set_name: 'Set the template name. Args: { name }.',
  set_background: 'Set the template background colour. Args: { colour }.',
  set_business_entity:
    'Set the advertiser id and load its fonts when no shadow brand is set. Args: { businessEntityId }. Apply this on its own, not inside changes. Read brand again for fontFamilies.',
  set_brand_kit: 'Set or clear the brand kit. Args: { brandKitId, brandKit? }.',
  select_element: 'Select an element. Does not change workingRevision. Args: { elementId }.',
  rename_element: 'Rename an element. Args: { elementId, name }.',
  move_element: 'Move an element. Args: { elementId, x, y }.',
  reorder_element:
    'Change an element stacking order. Args: { elementId, command } where command is front, forward, backward, or back. The Figma frame SVG and root background vector cannot be reordered.',
  remove_element:
    'Remove an element. Its animation effects and tracks are removed too. Args: { elementId }.',
  add_element: 'Add one element. Args: { element } matching the editor element shape.',
  update_element_data:
    'Merge fields into the current element data. Omitted fields stay. null clears one field. Args: { elementId, data }. A fontFamily change must also include fontWeight, fontStyle, fontStretch, and fontVariant from the brand read fonts list. Read the element again to see the result.',
  set_element_locked:
    'Set whether an element is locked. Args: { elementId, locked }. Locked layers cannot be dragged on the canvas. Rename and delete stay blocked in the sidebar; other sidebar edits including position still apply. Preview ignores lock.',
  set_element_hidden:
    'Set whether an element is hidden. Args: { elementId, hidden }. Hidden layers are omitted from Canvas, Preview, and readType preview. They remain on the document and can still be edited.',
  set_element_sources: 'Replace an element data-source bindings. Args: { elementId, sources }.',
  remove_element_source: 'Remove one binding. Args: { elementId, dataSourceId }.',
  add_data_source:
    'Add a template or market data source. Args: { type, dataType?, id? }. Apply this on its own, not inside changes.',
  remove_data_source:
    'Remove a data source by id. Args: { dataSourceId }. Apply this on its own, not inside changes.',
  set_display_options: 'Set one display-option group. Args: { key, value }.',
  set_element_date_format: 'Set one element date format. Args: { elementId, value }.',
  set_element_odds_format: 'Set one element odds format. Args: { elementId, value }.',
  set_element_penalties_format: 'Set one element penalties format. Args: { elementId, value }.',
  apply_animation_effect:
    'Apply a preset effect to an element. Args: { targetId, presetId, params?: { duration?, start?, easing?, distance? }, expectedWorkingRevision? }. Seconds for duration and start, pixels for distance. The same preset twice returns ANIMATION_DUPLICATE. A preset with the same phase and property replaces the old effect and lists it in replaced.',
  update_animation_effect:
    'Change an applied effect. Args: { effectId, params?, presetId?, expectedWorkingRevision? }. presetId must keep the role (entrance or exit).',
  remove_animation_effect:
    'Remove an applied effect. Removing the last one clears the animation. Args: { effectId, expectedWorkingRevision? }.',
  group_elements: 'Unsupported. Grouping and nested scenes stay out of this story.',
  commit_working_copy:
    'Open Confirm Save and return an instruction to wait. Does not write. Call again with userConfirmed true only if the user explicitly asked to save, or with cancel true to close the dialog without saving. Args: { expectedWorkingRevision, userConfirmed?, cancel? }. Apply this on its own, not inside changes.',
  stage_saved_version:
    'Load a saved version into the working copy only. Does not persist. Args: { restoreToken, expectedWorkingRevision, expectedPersistedRevision } from saved_version_preview. Apply on its own.',
};

export function isTemplateBuilderOperation(value: string): value is TemplateBuilderOperation {
  return (TEMPLATE_BUILDER_OPERATIONS as readonly string[]).includes(value);
}

export function isAnimationOperation(value: string): value is AnimationOperation {
  return (ANIMATION_OPERATIONS as readonly string[]).includes(value);
}

export function operationDomain(
  operation: TemplateBuilderOperation,
): TemplateBuilderOperationDomain {
  return DOMAINS[operation];
}

export function operationSummary(operation: TemplateBuilderOperation): string {
  return SUMMARIES[operation];
}

export function operationSupported(operation: TemplateBuilderOperation): boolean {
  return !(UNSUPPORTED_OPERATIONS as readonly string[]).includes(operation);
}

/** Supported and enabled for the open template. Animation needs both feature flags. */
export function operationAvailable(
  operation: TemplateBuilderOperation,
  availability: OperationAvailability,
): boolean {
  if (!operationSupported(operation)) {
    return false;
  }
  return operationDomain(operation) !== 'animation' || availability.animationsEnabled;
}
